import { useCallback, useEffect, useState } from "react";
import {
  Plus,
  Trash,
  Lightning,
  CheckCircle,
  Warning,
  Play,
  Plugs,
  ArrowRight,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Checkbox } from "../components/ui/checkbox";
import {
  listConnections,
  createConnection,
  deleteConnection,
  testConnection,
  generateFromConnection,
} from "../lib/api";
import { PLATFORMS } from "../lib/constants";

const AUTH_KINDS = [
  { value: "bearer", label: "Bearer token" },
  { value: "api_key", label: "API key (header)" },
  { value: "basic", label: "Basic auth (user + pass)" },
  { value: "query", label: "API key (query param)" },
  { value: "none", label: "No auth" },
];

const BLANK_FORM = {
  name: "",
  base_url: "",
  catalog_path: "",
  homepage_url: "",
  auth: { kind: "bearer", token: "", username: "", password: "", header_name: "", param_name: "" },
  items_path: "",
  mapping: { title: "", description: "", image: "", url: "", price: "" },
  max_items: 20,
};

export default function Connections() {
  const navigate = useNavigate();
  const [conns, setConns] = useState([]);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [form, setForm] = useState(BLANK_FORM);
  const [genPlatforms, setGenPlatforms] = useState({});   // {connId: [platform]}
  const [genImages, setGenImages] = useState({});         // {connId: bool}

  const load = useCallback(async () => {
    try {
      setConns(await listConnections());
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to load connections");
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const create = async () => {
    if (!form.name.trim() || !form.base_url.trim() || !form.homepage_url.trim()) {
      toast.error("Name, base URL, and homepage URL are required");
      return;
    }
    setSaving(true);
    try {
      const conn = await createConnection(form);
      // Immediately test the new connection so the user sees a status
      try {
        await testConnection(conn.id);
      } catch (err) {
        // Connection was saved; the server persists the failed test status
        // and the UI shows it via the last_status field. Log for debugging.
        console.warn("Post-save connection test failed:", err?.message || err);
      }
      toast.success("Connection saved · testing…");
      setOpen(false);
      setForm(BLANK_FORM);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const runTest = async (id) => {
    setBusyId(id);
    try {
      const r = await testConnection(id);
      toast.success(`Fetched ${r.count} items`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Test failed");
      load();
    } finally {
      setBusyId(null);
    }
  };

  const genAds = async (id) => {
    const platforms = genPlatforms[id]?.length ? genPlatforms[id] : ["google", "meta"];
    setBusyId(id);
    try {
      const adSet = await generateFromConnection(id, {
        platforms,
        tone: "energetic",
        audience: "",
        generate_images: !!genImages[id],
      });
      toast.success(`Ads generated · redirecting to results`);
      // Route to Generator page — result is loaded via id
      navigate(`/?ad_set_id=${adSet.id}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Generation failed");
    } finally {
      setBusyId(null);
      load();
    }
  };

  const remove = async (id) => {
    if (!confirm("Delete this connection?")) return;
    try {
      await deleteConnection(id);
      toast.success("Deleted");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Delete failed");
    }
  };

  const togglePlatform = (connId, p) => {
    setGenPlatforms((state) => {
      const cur = state[connId] || ["google", "meta"];
      return {
        ...state,
        [connId]: cur.includes(p) ? cur.filter((x) => x !== p) : [...cur, p],
      };
    });
  };

  return (
    <div className="max-w-6xl" data-testid="connections-page">
      <div className="mb-10 flex items-end justify-between gap-3 flex-wrap">
        <div>
          <div className="label-mono mb-3">integrations</div>
          <h1 className="font-display text-4xl sm:text-5xl font-light tracking-tighter">
            API <span className="font-black text-[#ccff00]">Connections</span>
          </h1>
          <p className="text-zinc-500 mt-3 max-w-2xl text-sm">
            Paste an API token from any affiliate program. AdForge pulls the
            catalog, generates ads from it, and points every click to the
            program&apos;s homepage.
          </p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <button
              data-testid="new-connection-btn"
              className="bg-[#ccff00] text-black hover:bg-[#b3e600] font-semibold py-3 px-5 flex items-center gap-2 transition-colors"
            >
              <Plus size={14} weight="bold" />
              New connection
            </button>
          </DialogTrigger>
          <DialogContent
            className="bg-[#0a0a0a] border border-white/10 rounded-none max-w-xl max-h-[85vh] overflow-y-auto"
            data-testid="new-connection-dialog"
          >
            <DialogHeader>
              <DialogTitle className="font-display text-2xl flex items-center gap-2">
                <Plugs size={22} weight="fill" className="text-[#ccff00]" />
                New API connection
              </DialogTitle>
              <DialogDescription className="text-zinc-500 text-sm">
                All requests go server-to-server. Tokens never touch the browser.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-3">
              <div>
                <div className="label-mono mb-2">name</div>
                <Input
                  data-testid="conn-name-input"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="e.g. Acme Affiliate"
                  className="bg-[#121212] border-white/10 h-10 rounded-none"
                />
              </div>
              <div>
                <div className="label-mono mb-2">base url</div>
                <Input
                  data-testid="conn-base-url-input"
                  value={form.base_url}
                  onChange={(e) => setForm({ ...form, base_url: e.target.value })}
                  placeholder="https://api.acme.com/v1"
                  className="bg-[#121212] border-white/10 h-10 rounded-none font-mono text-xs"
                />
              </div>
              <div>
                <div className="label-mono mb-2">catalog path (optional)</div>
                <Input
                  data-testid="conn-catalog-input"
                  value={form.catalog_path}
                  onChange={(e) => setForm({ ...form, catalog_path: e.target.value })}
                  placeholder="/products"
                  className="bg-[#121212] border-white/10 h-10 rounded-none font-mono text-xs"
                />
                <div className="text-[10px] text-zinc-600 mt-1 font-mono">appended to base URL</div>
              </div>
              <div>
                <div className="label-mono mb-2">homepage url (ad destination)</div>
                <Input
                  data-testid="conn-homepage-input"
                  value={form.homepage_url}
                  onChange={(e) => setForm({ ...form, homepage_url: e.target.value })}
                  placeholder="https://acme.com/"
                  className="bg-[#121212] border-white/10 h-10 rounded-none font-mono text-xs"
                />
              </div>

              <div>
                <div className="label-mono mb-2">auth type</div>
                <Select
                  value={form.auth.kind}
                  onValueChange={(v) => setForm({ ...form, auth: { ...form.auth, kind: v } })}
                >
                  <SelectTrigger
                    data-testid="conn-auth-select"
                    className="bg-[#121212] border-white/10 rounded-none h-10"
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#0a0a0a] border-white/10 rounded-none">
                    {AUTH_KINDS.map((a) => (
                      <SelectItem key={a.value} value={a.value}>
                        {a.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {form.auth.kind === "basic" ? (
                <div className="grid grid-cols-2 gap-2">
                  <Input
                    data-testid="conn-username-input"
                    placeholder="username"
                    value={form.auth.username}
                    onChange={(e) =>
                      setForm({ ...form, auth: { ...form.auth, username: e.target.value } })
                    }
                    className="bg-[#121212] border-white/10 h-10 rounded-none font-mono text-xs"
                  />
                  <Input
                    data-testid="conn-password-input"
                    type="password"
                    placeholder="password"
                    value={form.auth.password}
                    onChange={(e) =>
                      setForm({ ...form, auth: { ...form.auth, password: e.target.value } })
                    }
                    className="bg-[#121212] border-white/10 h-10 rounded-none font-mono text-xs"
                  />
                </div>
              ) : form.auth.kind !== "none" ? (
                <>
                  <Input
                    data-testid="conn-token-input"
                    type="password"
                    placeholder="paste API token"
                    value={form.auth.token}
                    onChange={(e) =>
                      setForm({ ...form, auth: { ...form.auth, token: e.target.value } })
                    }
                    className="bg-[#121212] border-white/10 h-10 rounded-none font-mono text-xs"
                  />
                  {(form.auth.kind === "api_key" || form.auth.kind === "query") && (
                    <Input
                      data-testid="conn-header-name-input"
                      placeholder={
                        form.auth.kind === "api_key" ? "header name (default X-API-Key)" : "query param name (default api_key)"
                      }
                      value={form.auth.kind === "api_key" ? form.auth.header_name : form.auth.param_name}
                      onChange={(e) => {
                        const key = form.auth.kind === "api_key" ? "header_name" : "param_name";
                        setForm({ ...form, auth: { ...form.auth, [key]: e.target.value } });
                      }}
                      className="bg-[#121212] border-white/10 h-10 rounded-none font-mono text-xs"
                    />
                  )}
                </>
              ) : null}

              <div className="pt-2 border-t border-white/5">
                <div className="label-mono mb-2">items path (jsonpath, optional)</div>
                <Input
                  data-testid="conn-items-path-input"
                  value={form.items_path}
                  onChange={(e) => setForm({ ...form, items_path: e.target.value })}
                  placeholder="data.products (blank = root list)"
                  className="bg-[#121212] border-white/10 h-10 rounded-none font-mono text-xs"
                />
                <div className="text-[10px] text-zinc-600 mt-1 font-mono">
                  Dotted path to the list. e.g. <code>response.items</code>.
                </div>
              </div>

              <div>
                <div className="label-mono mb-2">field mapping (their → ours)</div>
                <div className="space-y-1.5">
                  {["title", "description", "image", "url", "price"].map((k) => (
                    <div key={k} className="flex items-center gap-2">
                      <span className="text-[10px] font-mono text-zinc-500 w-20 uppercase tracking-wider">
                        {k}
                      </span>
                      <Input
                        data-testid={`conn-map-${k}-input`}
                        value={form.mapping[k] || ""}
                        onChange={(e) =>
                          setForm({ ...form, mapping: { ...form.mapping, [k]: e.target.value } })
                        }
                        placeholder={`their field for "${k}"`}
                        className="bg-[#121212] border-white/10 h-9 rounded-none font-mono text-xs flex-1"
                      />
                    </div>
                  ))}
                </div>
                <div className="text-[10px] text-zinc-600 mt-1 font-mono">
                  Leave blank to auto-guess (title/name/description/summary/image_url/permalink).
                </div>
              </div>

              <div className="flex items-center gap-2">
                <span className="text-[10px] font-mono text-zinc-500 w-20 uppercase tracking-wider">
                  max items
                </span>
                <Input
                  data-testid="conn-max-items-input"
                  type="number"
                  min={1}
                  max={100}
                  value={form.max_items}
                  onChange={(e) => setForm({ ...form, max_items: parseInt(e.target.value || "20", 10) })}
                  className="bg-[#121212] border-white/10 h-9 rounded-none font-mono text-xs w-24"
                />
              </div>
            </div>
            <DialogFooter>
              <button
                data-testid="conn-save-btn"
                onClick={create}
                disabled={saving}
                className="bg-[#ccff00] text-black hover:bg-[#b3e600] disabled:opacity-40 font-semibold py-2.5 px-4"
              >
                {saving ? "Saving…" : "Save & test"}
              </button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {conns.length === 0 && (
        <div className="text-center py-16 text-zinc-500 font-mono text-sm">
          No connections yet. Click <span className="text-[#ccff00]">+ New connection</span> to paste an API token.
        </div>
      )}

      <div className="space-y-4">
        {conns.map((c) => {
          const platforms = genPlatforms[c.id] || ["google", "meta"];
          return (
            <div
              key={c.id}
              data-testid={`connection-row-${c.id}`}
              className="bg-[#0a0a0a] border border-white/10 hover:border-white/20 p-5 transition-colors"
            >
              <div className="flex items-start justify-between gap-3 mb-3">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <Plugs
                      size={14}
                      weight="fill"
                      className={c.last_status === "ok" ? "text-emerald-400" : c.last_status === "error" ? "text-red-400" : "text-zinc-500"}
                    />
                    <div className="font-display font-bold text-white truncate">
                      {c.name}
                    </div>
                    {c.last_status === "ok" && (
                      <span className="text-[9px] font-mono uppercase tracking-wider px-1.5 py-0.5 bg-emerald-500/10 text-emerald-300 border border-emerald-400/30">
                        connected · {c.last_item_count} items
                      </span>
                    )}
                    {c.last_status === "error" && (
                      <span className="text-[9px] font-mono uppercase tracking-wider px-1.5 py-0.5 bg-red-500/10 text-red-300 border border-red-400/30">
                        error
                      </span>
                    )}
                  </div>
                  <div className="font-mono text-[10px] text-zinc-500 truncate">
                    {c.base_url}
                    {c.catalog_path}
                  </div>
                  <div className="font-mono text-[10px] text-zinc-500 truncate mt-0.5 flex items-center gap-1">
                    <ArrowRight size={9} />→ ads land on <span className="text-[#ccff00]">{c.homepage_url}</span>
                  </div>
                  {c.last_error && (
                    <div className="mt-2 flex items-start gap-1.5 text-[11px] text-red-400 font-mono">
                      <Warning size={12} weight="fill" className="mt-0.5 flex-shrink-0" />
                      <span className="break-all">{c.last_error}</span>
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-1.5 flex-shrink-0">
                  <button
                    data-testid={`test-${c.id}`}
                    onClick={() => runTest(c.id)}
                    disabled={busyId === c.id}
                    className="p-2 bg-[#121212] border border-white/10 hover:border-cyan-400 hover:text-cyan-400 transition-colors disabled:opacity-40"
                    title="Test connection"
                  >
                    <CheckCircle size={13} />
                  </button>
                  <button
                    data-testid={`delete-conn-${c.id}`}
                    onClick={() => remove(c.id)}
                    className="p-2 bg-[#121212] border border-white/10 hover:border-red-500 hover:text-red-500 transition-colors"
                    title="Delete"
                  >
                    <Trash size={13} />
                  </button>
                </div>
              </div>

              {/* Generate ads panel */}
              <div className="pt-3 border-t border-white/5 flex items-center gap-2 flex-wrap">
                <div className="label-mono">generate</div>
                <div className="flex items-center gap-1 flex-wrap">
                  {PLATFORMS.map((p) => {
                    const active = platforms.includes(p.id);
                    return (
                      <button
                        key={p.id}
                        data-testid={`conn-${c.id}-platform-${p.id}`}
                        onClick={() => togglePlatform(c.id, p.id)}
                        className={`text-[9px] font-mono uppercase tracking-wider py-1 px-2 border transition-colors ${
                          active
                            ? "bg-[#ccff00] text-black border-[#ccff00] font-bold"
                            : "bg-transparent text-zinc-400 border-white/10 hover:border-white/30"
                        }`}
                      >
                        {p.label}
                      </button>
                    );
                  })}
                </div>
                <label className="flex items-center gap-1.5 cursor-pointer text-xs text-zinc-400 ml-2">
                  <Checkbox
                    data-testid={`conn-${c.id}-images-checkbox`}
                    checked={!!genImages[c.id]}
                    onCheckedChange={(v) => setGenImages({ ...genImages, [c.id]: !!v })}
                  />
                  images
                </label>
                <div className="flex-1" />
                <button
                  data-testid={`generate-from-${c.id}`}
                  onClick={() => genAds(c.id)}
                  disabled={busyId === c.id || c.last_status === "error"}
                  className="bg-[#ccff00] text-black hover:bg-[#b3e600] disabled:opacity-40 font-semibold py-2 px-3 flex items-center gap-1.5 text-xs transition-colors"
                >
                  <Lightning size={12} weight="fill" />
                  {busyId === c.id ? "generating…" : "Generate ads"}
                </button>
              </div>
              {c.last_ad_set_id && (
                <div className="mt-2 pt-2 border-t border-white/5 flex items-center gap-1.5 text-[11px] text-emerald-400 font-mono">
                  <Play size={11} weight="fill" />
                  last ad set:{" "}
                  <button
                    onClick={() => navigate(`/?ad_set_id=${c.last_ad_set_id}`)}
                    className="text-[#ccff00] hover:underline"
                  >
                    {c.last_ad_set_id.slice(0, 8)}
                  </button>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
